// domain/services/programIdentity.js

const normalizedName = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : null);

/**
 * Is this stored program the one a program of this name at this destination means?
 *
 * ONE PREDICATE, BECAUSE THE WARNING AND THE WRITE HAVE TO AGREE. An import's preview uses it to tell
 * the user a program will be overwritten; its save path uses it to decide whether to overwrite or
 * create. They were two copies that drifted: one normalized an absent `client` to null and the
 * other compared it raw, so a program stored without the field was warned about and then
 * duplicated instead of overwritten. A looser match warns about programs nothing will touch, a
 * tighter one stays silent about ones it will.
 *
 * ABSENT AND NULL ARE THE SAME PLACE. A library program written before the field existed carries no
 * `client`, and one at the root carries no `parentFolder`. Both mean "not under one", which is what
 * a resolved destination of null means.
 *
 * A CLIENT'S PROGRAM MATCHES IN WHICHEVER FOLDER IT WAS FILED. The client Programs page always
 * imports into the root, and a clinician files programs into folders afterwards, so requiring the
 * folder to match duplicated every program they had filed. In the Library the folder is the
 * destination the user chose, so it still has to match there.
 *
 * @param {Object} existing - a stored program
 * @param {{programName: *, destinationClient: ?string, destinationFolder: ?string}} destination
 * @returns {boolean}
 */
export function isProgramAtDestination(existing, { programName, destinationClient, destinationFolder }) {
  const name = normalizedName(programName);

  // Nothing is at a destination the file cannot name, and no stored program without a usable name
  // is the one it means. Reading the name off either side without this is what threw on a legacy
  // record that has none.
  if (name === null || normalizedName(existing?.programName) !== name) {
    return false;
  }

  if ((existing?.client ?? null) !== (destinationClient ?? null)) {
    return false;
  }
  return (destinationClient ?? null) !== null
    || (existing?.parentFolder ?? null) === (destinationFolder ?? null);
} // isProgramAtDestination()

/**
 * Does the name the file gives match more than one of the client's programs?
 *
 * A client may have two programs of the same name in different folders, and with a match that
 * ignores the folder nothing tells them apart. The import refuses rather than picking one: updating
 * the wrong program puts session history and targets on a program the clinician did not mean.
 *
 * @param {Object[]} existingData - stored programs
 * @param {{programName: *, destinationClient: ?string, destinationFolder: ?string}} destination
 * @returns {boolean}
 */
export function isProgramNameAmbiguous(existingData, destination) {
  return (destination.destinationClient ?? null) !== null
    && existingData.filter((existing) => isProgramAtDestination(existing, destination)).length > 1;
} // isProgramNameAmbiguous()
